const runtimeEnv = typeof __LOCS_ENV__ === 'object' && __LOCS_ENV__ != null
  ? __LOCS_ENV__
  : {};
const isDevelopmentBuild = typeof __LOCS_DEV__ === 'boolean' && __LOCS_DEV__;

/**
 * Resolve an API base URL.
 * Priority: runtime env var (__LOCS_ENV__) → inferred from window.location.
 * The inferred URL uses the same scheme, host and port as the current page
 * so that the app works on any LAN address without extra configuration.
 */
function resolveApiBaseUrl(): string {
  if (runtimeEnv.LOCS_API_BASE_URL) {
    return runtimeEnv.LOCS_API_BASE_URL;
  }
  // Infer absolute origin from the current page (scheme + host + port), then
  // append /api to match the server API path prefix.
  return `${window.location.origin}/api`;
}

/**
 * Resolve a WebSocket URL.
 * Priority: runtime env var (__LOCS_ENV__) → inferred from window.location.
 * The inferred URL uses the same host and port as the current page, converting
 * http(s) to ws(s) and appending the given path.
 */
function resolveWsUrl(envUrl: string | undefined, path: string): string {
  if (envUrl) {
    return envUrl;
  }
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}${path}`;
}

export const defaultApi = resolveApiBaseUrl();
export const defaultMsgWs = resolveWsUrl(runtimeEnv.LOCS_MSG_WS_URL, '/msg-ws');
export const defaultFileWs = resolveWsUrl(runtimeEnv.LOCS_FILE_WS_URL, '/file-ws');
export const defaultAes = runtimeEnv.LOCS_IM_CRYPTO_AES_KEY || runtimeEnv.LOCS_AES_KEY || '';
export const httpCryptoEnabled = String(runtimeEnv.LOCS_HTTP_CRYPTO_ENABLED || 'true').toLowerCase() !== 'false';
export const httpCryptoAes = runtimeEnv.LOCS_HTTP_CRYPTO_AES_KEY || defaultAes;
export const defaultCdn = runtimeEnv.LOCS_CDN_BASE_URL || 'http://localhost';
export const ffmpegCoreUrl = runtimeEnv.LOCS_FFMPEG_CORE_URL || '/vendor/ffmpeg/ffmpeg-core.js';
export const ffmpegWasmUrl = runtimeEnv.LOCS_FFMPEG_WASM_URL
  || 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.wasm';
export const oauthGoogleClientId = runtimeEnv.LOCS_OAUTH_GOOGLE_CLIENT_ID || '';
export const oauthAppleClientId = runtimeEnv.LOCS_OAUTH_APPLE_CLIENT_ID || '';
export const oauthAppleRedirectUri = runtimeEnv.LOCS_OAUTH_APPLE_REDIRECT_URI || '';
export const oauthTwitterClientId = runtimeEnv.LOCS_OAUTH_TWITTER_CLIENT_ID || '';
export const googleMapsApiKey = runtimeEnv.LOCS_GOOGLE_MAPS_API_KEY || '';
export const googleMapsMapId = runtimeEnv.LOCS_GOOGLE_MAPS_MAP_ID || (isDevelopmentBuild ? 'DEMO_MAP_ID' : '');
export const mapProvider = runtimeEnv.LOCS_MAP_PROVIDER === 'google' ? 'google' : 'openfreemap';
export const openFreeMapStyleUrl = runtimeEnv.LOCS_OPENFREEMAP_STYLE_URL
  || 'https://tiles.openfreemap.org/styles/liberty';
export const openFreeMapDarkStyleUrl = runtimeEnv.LOCS_OPENFREEMAP_DARK_STYLE_URL
  || 'https://tiles.openfreemap.org/styles/dark';

export const MIN_PLAZA_REFRESH_MS = 700;
export const POST_CONTENT_MAX = 2000;

export const EMOJI_LICENSE_ITEMS = [
  {
    name: 'Fluent Emoji',
    license: 'MIT',
    note: 'Copyright Microsoft Corporation. 商用可用，需保留版权与许可声明。',
  },
] as const;

export const SESSION_TYPE_SINGLE = 1;
export const SESSION_TYPE_GROUP = 2;
export const USER_STATUS_ONLINE = 1;
export const USER_STATUS_OFFLINE = 2;
export const USER_STATUS_LEAVE = 3;
export const MESSAGE_IMAGE_PREFIX = '&$#@~^@[{:';
export const MESSAGE_IMAGE_SUFFIX = ':}]&$~@#@';

export type ConnState = 'idle' | 'connecting' | 'online' | 'error';

export type LogEntry = {
  id: number;
  ts: string;
  msg: string;
  kind: 'info' | 'in' | 'out' | 'error' | 'success' | 'warn';
};
